// ImprovedAuthWrapper.js - Much better landing page design
import React, { useState, useEffect } from 'react';
import { Brain, Loader2, Mic, Sparkles, MessageSquare, Zap, Play, ChevronRight, Star, Users, Shield } from 'lucide-react';
import { signOutUser, waitForAuthState } from '../services/firebase';
import AuthModal from './AuthModal';
import KnowledgeManagementApp from '../KnowledgeManagementApp';

const AuthWrapper = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAuthModal, setShowAuthModal] = useState(false);

    useEffect(() => {
        const initAuth = async () => {
            try {
                const initialUser = await waitForAuthState();
                setUser(initialUser);

                if (!initialUser) {
                    setShowAuthModal(false); // Don't auto-show modal, let them explore first
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const handleAuthSuccess = (authenticatedUser) => {
        setUser(authenticatedUser);
        setShowAuthModal(false);
    };

    const handleSignOut = async () => {
        try {
            await signOutUser();
            setUser(null);
            setShowAuthModal(false);
        } catch (error) {
            console.error('Sign out error:', error);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6 animate-pulse">
                        <Brain className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">DendriteAI</h1>
                    <div className="flex items-center justify-center space-x-3 text-blue-200">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-lg">Initializing your AI brain...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <>
                <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 text-slate-900 w-full">
                    {/* Navigation */}
                    <nav className="relative z-10 px-6 py-6 w-full">
                        <div className="w-full flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center">
                                    <Brain className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-slate-900">DendriteAI</span>
                            </div>
                            <button
                                onClick={() => setShowAuthModal(true)}
                                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl transition-all duration-300 font-medium"
                            >
                                Sign In
                            </button>
                        </div>
                    </nav>

                    {/* Hero Section - Full viewport width */}
                    <div className="relative px-6 h-screen flex items-center w-full">
                        {/* Background Effects */}
                        <div className="absolute inset-0 overflow-hidden w-full">
                            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-300/30 rounded-full blur-3xl animate-pulse"></div>
                            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-slate-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                        </div>

                        <div className="relative z-10 w-full text-center">
                            <div className="mb-8">
                                <div className="inline-flex items-center space-x-2 bg-slate-200/80 backdrop-blur-sm border border-slate-300/50 rounded-full px-4 py-2 mb-6">
                                    <Sparkles className="w-4 h-4 text-slate-700" />
                                    <span className="text-slate-700 text-sm font-medium">Powered by Advanced AI</span>
                                </div>
                                <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900 bg-clip-text text-transparent leading-tight">
                                    Your AI-Powered
                                    <br />
                                    Second Brain
                                </h1>
                                <p className="text-xl md:text-2xl text-slate-600 mb-8 max-w-4xl mx-auto leading-relaxed">
                                    Transform your voice into organized knowledge. Record, transcribe, and discover insights with the power of artificial intelligence.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                                <button
                                    onClick={() => setShowAuthModal(true)}
                                    className="group px-8 py-4 bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 text-white rounded-2xl transition-all duration-300 font-semibold text-lg shadow-xl shadow-slate-900/25 flex items-center space-x-2"
                                >
                                    <span>Start Building Your Brain</span>
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="group px-8 py-4 bg-white/80 backdrop-blur-sm border border-slate-300 rounded-2xl hover:bg-white transition-all duration-300 font-semibold text-lg flex items-center space-x-2 text-slate-700">
                                    <Play className="w-5 h-5" />
                                    <span>Watch Demo</span>
                                </button>
                            </div>

                            {/* Social Proof */}
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-slate-500">
                                <div className="flex items-center space-x-2">
                                    <Users className="w-5 h-5" />
                                    <span>1,000+ users trust us</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Star className="w-5 h-5 text-yellow-500" />
                                    <span>4.9/5 rating</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Shield className="w-5 h-5" />
                                    <span>Enterprise secure</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features Section - Full viewport width */}
                    <div className="relative px-6 h-screen bg-white/50 backdrop-blur-sm flex items-center w-full">
                        <div className="w-full">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-slate-800 to-slate-700 bg-clip-text text-transparent">
                                    Revolutionary Features
                                </h2>
                                <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                    Experience the future of knowledge management with AI-powered tools designed for modern productivity.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                                {/* Smart Recording */}
                                <div className="group p-8 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-3xl hover:border-slate-300 transition-all duration-500 hover:transform hover:scale-105 shadow-lg hover:shadow-xl">
                                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                        <Mic className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-slate-900">Smart Recording</h3>
                                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                        Record meetings, lectures, or ideas and watch as our AI transforms speech into organized, searchable knowledge.
                                    </p>
                                    <div className="flex items-center space-x-2 text-slate-700 font-medium">
                                        <Zap className="w-4 h-4" />
                                        <span>Instant transcription</span>
                                    </div>
                                </div>

                                {/* AI Insights */}
                                <div className="group p-8 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-3xl hover:border-slate-300 transition-all duration-500 hover:transform hover:scale-105 shadow-lg hover:shadow-xl">
                                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                        <Sparkles className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-slate-900">AI Insights</h3>
                                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                        Automatically extract action items, key decisions, and important topics from your recordings with advanced AI analysis.
                                    </p>
                                    <div className="flex items-center space-x-2 text-slate-700 font-medium">
                                        <Brain className="w-4 h-4" />
                                        <span>Smart analysis</span>
                                    </div>
                                </div>

                                {/* Smart Search */}
                                <div className="group p-8 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-3xl hover:border-slate-300 transition-all duration-500 hover:transform hover:scale-105 shadow-lg hover:shadow-xl">
                                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                        <MessageSquare className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-4 text-slate-900">Intelligent Search</h3>
                                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                        Chat with your knowledge base using natural language. Find exactly what you need across all your content instantly.
                                    </p>
                                    <div className="flex items-center space-x-2 text-slate-700 font-medium">
                                        <MessageSquare className="w-4 h-4" />
                                        <span>Natural language queries</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Section - Full viewport width */}
                    <div className="relative px-6 h-screen bg-gradient-to-r from-slate-800 to-slate-900 flex items-center w-full">
                        <div className="w-full text-center">
                            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                                Ready to Supercharge Your Brain?
                            </h2>
                            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
                                Join thousands of professionals who are already using DendriteAI to organize their thoughts and unlock their productivity.
                            </p>
                            <button
                                onClick={() => setShowAuthModal(true)}
                                className="group px-10 py-5 bg-white text-slate-900 hover:bg-slate-50 rounded-2xl transition-all duration-300 font-bold text-xl shadow-2xl flex items-center space-x-3 mx-auto"
                            >
                                <span>Get Started Free</span>
                                <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <p className="text-slate-400 mt-4">No credit card required • Free forever plan available</p>
                        </div>
                    </div>
                </div>

                <AuthModal
                    showAuthModal={showAuthModal}
                    setShowAuthModal={setShowAuthModal}
                    onAuthSuccess={handleAuthSuccess}
                />
            </>
        );
    }

    // User is authenticated, show the main app
    return (
        <KnowledgeManagementApp
            user={user}
            onSignOut={handleSignOut}
        />
    );
};

export default AuthWrapper;